import type { PillTone } from '@bcecloud/portal-ui';
import { getList, sendJson, type PaginationMeta } from './api';

/**
 * Sales → Payments: what every contracted event is owed across both payment
 * rails (Sertifi and Stripe). Mirrors `apps/api/src/lib/payments/receivables.ts`.
 */

/** The page's tabs. */
export const RECEIVABLE_FILTER = {
  outstanding: 'outstanding',
  overdue: 'overdue',
  paid: 'paid',
  all: 'all',
} as const;
export type ReceivableFilter = (typeof RECEIVABLE_FILTER)[keyof typeof RECEIVABLE_FILTER];

export const RECEIVABLE_STATUS = {
  paid: 'paid',
  outstanding: 'outstanding',
  overdue: 'overdue',
} as const;
export type ReceivableStatus = (typeof RECEIVABLE_STATUS)[keyof typeof RECEIVABLE_STATUS];

export const PAYMENT_RAIL = {
  stripe: 'stripe',
  sertifi: 'sertifi',
} as const;
export type PaymentRail = (typeof PAYMENT_RAIL)[keyof typeof PAYMENT_RAIL];

/** Sortable columns (the API maps each to an event field). */
export const RECEIVABLE_SORT_FIELDS = {
  client: 'client',
  eventDate: 'eventDate',
  contracted: 'contracted',
  owed: 'owed',
  dueDate: 'dueDate',
} as const;

/** Status → pill, so where a balance stands reads at a glance. */
export const RECEIVABLE_STATUS_PILLS: Readonly<Record<ReceivableStatus, { readonly label: string; readonly tone: PillTone }>> = {
  [RECEIVABLE_STATUS.paid]: { label: 'Paid', tone: 'success' },
  [RECEIVABLE_STATUS.outstanding]: { label: 'Outstanding', tone: 'info' },
  [RECEIVABLE_STATUS.overdue]: { label: 'Overdue', tone: 'danger' },
};

/** How the client pays, in the reader's words. */
export const PAYMENT_RAIL_LABELS: Readonly<Record<PaymentRail, string>> = {
  [PAYMENT_RAIL.stripe]: 'BCEcloud (card or ACH)',
  [PAYMENT_RAIL.sertifi]: 'Sertifi',
};

/** One row on Sales → Payments. */
export interface ReceivableRow {
  readonly eventId: string;
  readonly contractNumber: string | null;
  readonly opportunityId: string | null;
  readonly eventName: string | null;
  readonly accountName: string | null;
  readonly eventDate: string | null;
  readonly stageName: string | null;
  readonly contracted: number;
  readonly paid: number;
  readonly owed: number;
  readonly dueDate: string | null;
  readonly status: ReceivableStatus;
  readonly rail: PaymentRail;
  readonly unsyncedPaymentIds: readonly string[];
}

export interface ListReceivablesParams {
  readonly filter: ReceivableFilter;
  readonly search?: string;
  readonly sort: string;
  readonly dir: 'asc' | 'desc';
  readonly page: number;
  readonly limit: number;
}

/** GET /payments/receivables (needs the manage_payments grant). */
export function listReceivables(
  params: ListReceivablesParams,
  signal?: AbortSignal,
): Promise<{ items: ReceivableRow[]; pagination: PaginationMeta }> {
  return getList<ReceivableRow>('/payments/receivables', { ...params }, signal);
}

/** Retries one Stripe payment's Salesforce write-back. */
export function retryPaymentSync(paymentId: string): Promise<{ synced: boolean }> {
  return sendJson('post', `/payments/ledger/${paymentId}/salesforce-sync`, {});
}
